import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import BaseModal from 'components/Modals/BaseModal';
import BaseTable from 'components/Tables/BaseTable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PageNumber from 'components/PageNumber/PageNumber';
import { Badge, Button, Form, Spinner } from 'react-bootstrap';
import { COMMAND_OPTIONS, HISTORY_ROWS_PER_PAGE } from '../config/ServiceManager.config';
import styles from '../css/ServiceManager.module.css';
import { fetchCommandHistoryOp } from '../ops/ServiceManager.ops';
import type { CommandHistoryRow, ServiceRecord } from '../types/ServiceManager.types';
import { getCommandVariant } from '../utils/ServiceManager.utils';

// '' is the "All …" row. react-select has no empty option and these selects are not
// clearable, so without a real reset row a picked filter could never be undone.
const allOption = (label: string) => ({ value: '', label });

const RESULT_FILTER_OPTIONS = [
  allOption('All Results'),
  { value: 'ok', label: 'OK' },
  { value: 'failed', label: 'Failed' },
];

interface CommandHistoryModalProps {
  show: boolean;
  onClose: () => void;
  services?: ServiceRecord[];
}

/**
 * Modal displaying paginated command history with service, command, and email filters.
 */
export default function CommandHistoryModal({
  show,
  onClose,
  services = [],
}: CommandHistoryModalProps) {
  const [rows, setRows] = useState<CommandHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [filterService, setFilterService] = useState('');
  const [filterCommand, setFilterCommand] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterTimestamp, setFilterTimestamp] = useState('');
  const [filterReason, setFilterReason] = useState('');
  const [filterResult, setFilterResult] = useState('');

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      // TODO: When backend pagination is added, replace limit:9999/offset:0 with
      // real page params and remove client-side filtering below — all filter fields
      // are already forwarded here and map directly to the SQL WHERE clause.
      const result = await fetchCommandHistoryOp({
        limit: 9999,
        offset: 0,
        ...(filterService && { service: filterService }),
        ...(filterCommand && { command: filterCommand }),
        ...(filterEmail && { user_email: filterEmail }),
        ...(filterTimestamp && { ts: filterTimestamp }),
        ...(filterReason && { reason: filterReason }),
        ...(filterResult === 'ok' && { success: true }),
        ...(filterResult === 'failed' && { success: false }),
      });
      setTotal(result.total);
      setRows(result.rows as CommandHistoryRow[]);
      setCurrentPage(1);
    } catch (err) {
      console.error('[CommandHistoryModal] Failed to fetch history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filterService, filterCommand, filterEmail, filterTimestamp, filterReason, filterResult]);

  useEffect(() => {
    if (show) fetchHistory();
  }, [show, fetchHistory]);

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterService, filterCommand, filterEmail, filterTimestamp, filterReason, filterResult]);

  const handleClose = () => {
    setRows([]);
    setTotal(0);
    setCurrentPage(1);
    setFilterService('');
    setFilterCommand('');
    setFilterEmail('');
    setFilterTimestamp('');
    setFilterReason('');
    setFilterResult('');
    onClose();
  };

  // TODO: Remove client-side filtering once backend pagination + SQL filtering is live.
  // The same params are already forwarded to the API in fetchHistory above.
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterService && r.service !== filterService) return false;
      if (filterCommand && r.command !== filterCommand) return false;
      if (filterEmail && !r.user_email?.toLowerCase().includes(filterEmail.toLowerCase()))
        return false;
      if (filterTimestamp) {
        const formatted = r.ts
          ? new Date(r.ts).toLocaleString(undefined, { timeZoneName: 'short' }).toLowerCase()
          : '';
        if (!formatted.includes(filterTimestamp.toLowerCase())) return false;
      }
      if (filterReason && !r.reason?.toLowerCase().includes(filterReason.toLowerCase()))
        return false;
      if (filterResult === 'ok' && !r.success) return false;
      if (filterResult === 'failed' && r.success) return false;
      return true;
    });
  }, [
    rows,
    filterService,
    filterCommand,
    filterEmail,
    filterTimestamp,
    filterReason,
    filterResult,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / HISTORY_ROWS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const displayedRows = filteredRows.slice(
    (safePage - 1) * HISTORY_ROWS_PER_PAGE,
    safePage * HISTORY_ROWS_PER_PAGE,
  );

  const columns = useMemo(
    () => [
      {
        header: 'Service',
        accessorKey: 'service' as const,
        Cell: ({ cell }: { cell: { getValue: () => string } }) => (
          <span className="text-theme">{cell.getValue()}</span>
        ),
      },
      {
        header: 'Command',
        accessorKey: 'command' as const,
        Cell: ({ cell }: { cell: { getValue: () => string } }) => (
          <Badge bg={getCommandVariant(cell.getValue())} className="text-capitalize">
            {cell.getValue()}
          </Badge>
        ),
      },
      {
        header: 'User',
        accessorKey: 'user_email' as const,
        Cell: ({ row }: { row: { original: CommandHistoryRow } }) => (
          <span className="text-theme-muted">
            {row.original.user_email || `User #${row.original.user_id}`}
          </span>
        ),
      },
      {
        header: 'Timestamp',
        accessorKey: 'ts' as const,
        Cell: ({ cell }: { cell: { getValue: () => string | undefined } }) => (
          <span className="text-theme-muted">
            {cell.getValue()
              ? new Date(cell.getValue()!).toLocaleString(undefined, { timeZoneName: 'short' })
              : '—'}
          </span>
        ),
      },
      {
        header: 'Reason',
        accessorKey: 'reason' as const,
        Cell: ({ cell }: { cell: { getValue: () => string | undefined } }) => (
          <span className="text-theme-muted">{cell.getValue() || '—'}</span>
        ),
      },
      {
        header: 'Result',
        accessorKey: 'success' as const,
        Cell: ({ row }: { row: { original: CommandHistoryRow } }) => (
          <Badge bg={row.original.success ? 'success' : 'danger'}>
            {row.original.success ? 'OK' : 'Failed'}
          </Badge>
        ),
      },
    ],
    [],
  );

  const serviceFilterOptions = [
    allOption('All Services'),
    ...services.map((service) => ({
      value: service.name,
      label: service.name,
    })),
  ];
  const commandFilterOptions = [
    allOption('All Commands'),
    ...COMMAND_OPTIONS.map((c) => ({ value: c, label: c })),
  ];

  return (
    <BaseModal
      show={show}
      handleShow={handleClose}
      size="xl"
      title={`Service Command History${total > 0 ? ` — ${total} records` : ''}`}
      contentStyle={{ padding: 0 }}
      footer={
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      }
    >
      {/* Filters */}
      <div className="d-flex gap-2 flex-wrap p-3 pb-2 flex-shrink-0">
        <BaseSelect
          wrapperClassName={styles.filterSelect}
          isSearchable
          selectProps={{
            'aria-label': 'Filter by service',
            options: serviceFilterOptions,
            value: findSelectOption(serviceFilterOptions, filterService),
            onChange: (option: { value: string } | null) => setFilterService(option?.value ?? ''),
          }}
        />

        <BaseSelect
          wrapperClassName={styles.filterSelectNarrow}
          isSearchable
          selectProps={{
            'aria-label': 'Filter by command',
            options: commandFilterOptions,
            value: findSelectOption(commandFilterOptions, filterCommand),
            onChange: (option: { value: string } | null) => setFilterCommand(option?.value ?? ''),
          }}
        />

        <BaseSelect
          wrapperClassName={styles.filterSelectNarrow}
          isSearchable={false}
          selectProps={{
            'aria-label': 'Filter by result',
            options: RESULT_FILTER_OPTIONS,
            value: findSelectOption(RESULT_FILTER_OPTIONS, filterResult),
            onChange: (option: { value: string } | null) => setFilterResult(option?.value ?? ''),
          }}
        />

        <Form.Control
          size="sm"
          placeholder="Filter by user..."
          className={styles.filterInput}
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
        />

        <Form.Control
          size="sm"
          placeholder="Filter by timestamp..."
          className={styles.filterInput}
          value={filterTimestamp}
          onChange={(e) => setFilterTimestamp(e.target.value)}
        />

        <Form.Control
          size="sm"
          placeholder="Filter by reason..."
          className={styles.filterInput}
          value={filterReason}
          onChange={(e) => setFilterReason(e.target.value)}
        />
      </div>

      {/* Table area */}
      <div>
        {/* Table */}
        {isLoading && rows.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="light" />
          </div>
        ) : (
          <BaseTable
            rows={displayedRows}
            columns={columns}
            onRowClick={() => {}}
            customEmptyMessage="No history found"
            customStyles={{
              container: { backgroundColor: 'transparent' },
              header: { backgroundColor: 'var(--color-primary-shadow)' },
              row: { backgroundColor: 'var(--color-primary-shadow)' },
              cell: { color: 'white', borderBottom: '1px solid #2a3438' },
            }}
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-2 p-2">
            <PageNumber
              pages={totalPages}
              value={safePage - 1}
              onChange={(p: number) => setCurrentPage(p + 1)}
            />
            <span className={`text-theme-muted ${styles.paginationCount}`}>
              {filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </BaseModal>
  );
}
